import { ArchiveAttachments } from '../components/ArchiveAttachments';
import { TastingSummary } from '../components/TastingSummary';
import { Link, useParams } from 'react-router-dom';
import { TastingNotes } from '../components/TastingNotes';
import { useQuery } from '@tanstack/react-query';
import { archiveApi } from '../api/archive';
import { eventDate } from '../lib/format';
import { historyApi } from '../api/historyContributions';
import { ProtectedFile } from '../components/ProtectedFile';

export function ArchiveEvent() {
  const { id = '' } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['archive-event', id],
    queryFn: () => archiveApi.get(id),
  });

  if (isLoading || !data) return <p className="text-cream-300/60">Loading…</p>;
  const { event, wines, participants } = data;

  return (
    <div>
      <Link to="/archive" className="text-xs text-cream-300/60 hover:text-gold-200">
        ← Archive
      </Link>
      <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-cream-50">{event.title}</h1>
          <p className="text-sm text-cream-300/60">
            {eventDate(event)} · {event.theme ?? '—'} · {event.regionLocation ?? 'Location unknown'}
          </p>
        </div>
        {/* Any member can add to the record of a past tasting: photos, scores, memories. */}
        <Link className="btn-ghost text-sm" to={`/contribute?eventId=${event.id}`} title="Photos, scorecards, memories — anything you have from this night">
          + Add to this tasting's record
        </Link>
      </div>

      <div className="card mb-6 overflow-x-auto">
        <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">Results</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-cream-300/50">
              <th className="py-2 pr-4">Rank</th>
              <th className="py-2 pr-4">Wine</th>
              <th className="py-2 pr-4">Producer</th>
              <th className="py-2 pr-4">Vintage</th>
              <th className="py-2 pr-4 text-right">Score</th>
              <th className="py-2 pr-4 text-right">Votes</th>
            </tr>
          </thead>
          <tbody>
            {wines.map((w) => (
              <tr key={w.id} className="border-t border-ink-800/60">
                <td className="py-2 pr-4 font-display text-gold-300">{w.groupRank ?? '—'}</td>
                <td className="py-2 pr-4 text-cream-50">
                  <span className="mr-2 text-cream-300/50">{w.letter}</span>
                  {w.catalogId ? (
                    <Link to={`/cellar/${w.catalogId}`} className="hover:text-gold-200">
                      {w.wineName ?? 'Unknown'}
                    </Link>
                  ) : (
                    (w.wineName ?? 'Unknown')
                  )}
                </td>
                <td className="py-2 pr-4 text-cream-200">{w.producerRaw ?? '—'}</td>
                <td className="py-2 pr-4 text-cream-200">{w.vintage ?? '—'}</td>
                <td className="py-2 pr-4 text-right tabular-nums text-cream-100">
                  {w.groupScore ?? '—'}
                </td>
                <td className="py-2 pr-4 text-right text-cream-300/50">{w.scoreCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2 className="mb-2 text-sm uppercase tracking-wide text-gold-400">
          Who scored ({participants.length})
        </h2>
        <p className="text-sm text-cream-200/80">
          {participants.map((p) => `${p.firstName} ${p.lastName}`).join(' · ') || '—'}
        </p>
      </div>
      {/* Members' notes — the other half of the record, never shown before. */}
      {(data?.wines ?? []).some((w) => w.wineName) ? (
        <section className="card space-y-5 p-6">
          <h2 className="font-display text-xl text-cream-50">Tasting notes</h2>
          {(data?.wines ?? [])
            .filter((w) => w.wineName)
            .map((w) => (
              <div key={w.id} className="border-t border-ink-800 pt-4 first:border-0 first:pt-0">
                <p className="mb-2 text-sm text-cream-100">
                  <span className="text-gold-400">{w.letter}</span> · {w.wineName}
                </p>
                <TastingNotes eventId={id!} wineId={w.id} />
              </div>
            ))}
        </section>
      ) : null}

      <ArchiveAttachments eventId={String(id)} />
      <SourceDocuments eventId={id} />
      <TastingSummary eventId={id} />
    </div>
  );
}

/** Documents and spreadsheets members contributed about this tasting (merged contributions only). */
function SourceDocuments({ eventId }: { eventId: string }) {
  const { data } = useQuery({ queryKey: ['contribution-event-files', eventId], queryFn: () => historyApi.eventFiles(eventId) });
  if (!data || data.length === 0) return null;
  return (
    <section className="card mt-6">
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">Source documents</h2>
      <div className="flex flex-wrap gap-3">
        {data.map((f) => (
          <ProtectedFile key={f.id} url={f.url} contentType={f.contentType} filename={f.filename} className="h-28 w-28 rounded object-cover" />
        ))}
      </div>
    </section>
  );
}
